// Model runs: did the scheduled economic-model runs actually land? (CIO 2026-11-02: "so we don't have to
// go to AML to see if it ran"; economic model only, the options models are not shown.)
//
// Training runs in Azure ML (workspace helion-ml) on a monthly schedule; the Function App's own retrain
// timer was retired 2026-10-01 (run_econ_pipeline). Scoring re-uses the latest model on a Function App
// timer (WEBSITE_TIME_ZONE America/Denver):
//
//   econ_train     AML helion-econ-monthly-train-v2      1st of the month 06:00 MT
//   econ_score     run_ml_refresh_timer                  daily 07:50 MT
//
// A training job publishes its manifest only when it SUCCEEDS, so a failed or skipped run leaves last
// month's manifest in place. That reads here as "Due" for a grace window after the scheduled time and
// "Overdue" after it; the reason for a failure is only in Azure ML studio. If a schedule moves in AML
// or a timer cron changes, change it here too.

export const MT_ZONE = "America/Denver";

export type Cadence =
  | { kind: "monthly"; hour: number; minute: number } // 1st of the month
  | { kind: "daily"; hour: number; minute: number; weekdaysOnly: boolean };

export type RunKey = "econ_train" | "econ_score";

export interface RunSpec {
  key: RunKey;
  step: string;
  source: string;
  cadence: Cadence;
  /** Hours after the scheduled time before a run that has not published counts as overdue. */
  graceHours: number;
}

export const RUN_SPECS: Record<RunKey, RunSpec> = {
  econ_train: {
    key: "econ_train",
    step: "Training",
    source: "Azure ML · helion-econ-monthly-train-v2",
    cadence: { kind: "monthly", hour: 6, minute: 0 },
    graceHours: 4,
  },
  econ_score: {
    key: "econ_score",
    step: "Daily scoring",
    source: "Function App · run_ml_refresh_timer",
    cadence: { kind: "daily", hour: 7, minute: 50, weekdaysOnly: false },
    graceHours: 2,
  },
};

export type RunState = "current" | "due" | "overdue" | "error" | "missing";

export interface RunCheck {
  spec: RunSpec;
  cadenceLabel: string;
  lastRunMs: number | null;
  /** Most recent scheduled time at or before now. */
  expectedMs: number;
  nextMs: number;
  state: RunState;
  note: string | null;
}

// ---- Mountain-time wall clock ------------------------------------------------------------------

type WallParts = { y: number; mo: number; d: number; h: number; mi: number };

const partsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: MT_ZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
});

function mtParts(ms: number): WallParts {
  const out: Record<string, number> = {};
  for (const p of partsFormatter.formatToParts(new Date(ms))) {
    if (p.type !== "literal") out[p.type] = Number(p.value);
  }
  return { y: out.year, mo: out.month, d: out.day, h: out.hour % 24, mi: out.minute };
}

/** Offset of Mountain time from UTC at an instant, in ms (negative: MT is behind UTC). */
function mtOffsetMs(ms: number): number {
  const p = mtParts(ms);
  const wallAsUtc = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi);
  return wallAsUtc - Math.floor(ms / 60000) * 60000;
}

/** The UTC instant of a Mountain-time wall-clock time. Month/day may overflow (Date.UTC normalises). */
export function mtWallToUtc(y: number, mo: number, d: number, h: number, mi: number): number {
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const first = guess - mtOffsetMs(guess);
  const second = guess - mtOffsetMs(first);
  return second;
}

function calendarDay(y: number, mo: number, d: number): { y: number; mo: number; d: number; weekday: number } {
  const t = new Date(Date.UTC(y, mo - 1, d));
  return { y: t.getUTCFullYear(), mo: t.getUTCMonth() + 1, d: t.getUTCDate(), weekday: t.getUTCDay() };
}

/** Most recent scheduled time at or before now, and the one after it. */
export function scheduleWindow(cadence: Cadence, nowMs: number): { expectedMs: number; nextMs: number } {
  const now = mtParts(nowMs);
  if (cadence.kind === "monthly") {
    let expectedMs = mtWallToUtc(now.y, now.mo, 1, cadence.hour, cadence.minute);
    let monthOffset = 0;
    if (expectedMs > nowMs) {
      monthOffset = -1;
      expectedMs = mtWallToUtc(now.y, now.mo - 1, 1, cadence.hour, cadence.minute);
    }
    const nextMs = mtWallToUtc(now.y, now.mo + monthOffset + 1, 1, cadence.hour, cadence.minute);
    return { expectedMs, nextMs };
  }

  const runsOn = (weekday: number) => !cadence.weekdaysOnly || (weekday !== 0 && weekday !== 6);
  let expectedMs = 0;
  for (let back = 0; back < 8; back += 1) {
    const day = calendarDay(now.y, now.mo, now.d - back);
    const t = mtWallToUtc(day.y, day.mo, day.d, cadence.hour, cadence.minute);
    if (t <= nowMs && runsOn(day.weekday)) {
      expectedMs = t;
      break;
    }
  }
  let nextMs = 0;
  for (let ahead = 0; ahead < 8; ahead += 1) {
    const day = calendarDay(now.y, now.mo, now.d + ahead);
    const t = mtWallToUtc(day.y, day.mo, day.d, cadence.hour, cadence.minute);
    if (t > nowMs && runsOn(day.weekday)) {
      nextMs = t;
      break;
    }
  }
  return { expectedMs, nextMs };
}

const stampFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: MT_ZONE,
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** "Sun, Nov 1, 6:04 AM MT" */
export function fmtMt(ms: number | null): string {
  return ms == null ? "—" : `${stampFormatter.format(new Date(ms))} MT`;
}

export function cadenceLabel(cadence: Cadence): string {
  const h12 = ((cadence.hour + 11) % 12) + 1;
  const at = `${h12}:${String(cadence.minute).padStart(2, "0")} ${cadence.hour < 12 ? "AM" : "PM"}`;
  if (cadence.kind === "monthly") return `1st of each month, ${at} MT`;
  return `${cadence.weekdaysOnly ? "Weekdays" : "Daily"}, ${at} MT`;
}

// ---- Status --------------------------------------------------------------------------------

function parseMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export function evaluateRun(
  spec: RunSpec,
  lastRunIso: string | null | undefined,
  nowMs: number,
  problem?: string | null,
): RunCheck {
  const { expectedMs, nextMs } = scheduleWindow(spec.cadence, nowMs);
  const lastRunMs = parseMs(lastRunIso);
  const base = { spec, cadenceLabel: cadenceLabel(spec.cadence), lastRunMs, expectedMs, nextMs };
  const training = spec.cadence.kind === "monthly";

  if (lastRunMs == null) {
    return { ...base, state: "missing", note: "No completed run on record." };
  }
  if (problem) {
    return { ...base, state: "error", note: problem };
  }
  if (lastRunMs >= expectedMs) {
    return { ...base, state: "current", note: null };
  }
  if (nowMs < expectedMs + spec.graceHours * 3600_000) {
    return { ...base, state: "due", note: `Scheduled ${fmtMt(expectedMs)}; not published yet.` };
  }
  return {
    ...base,
    state: "overdue",
    note: training
      ? `No new model since the ${fmtMt(expectedMs)} scheduled run. Check the job in Azure ML studio.`
      : `The ${fmtMt(expectedMs)} scoring run has not published.`,
  };
}

export function summarizeRuns(checks: RunCheck[]): { attention: number; due: number } {
  return {
    attention: checks.filter((c) => c.state === "overdue" || c.state === "error" || c.state === "missing").length,
    due: checks.filter((c) => c.state === "due").length,
  };
}

// ---- Feed adapter --------------------------------------------------------------------------

type FileMeta = { last_modified?: string | null } | null | undefined;

/** GET /econ/latest — blob metadata only; a manifest's last_modified is when that run published. */
export type EconLatestFeed = {
  files?: { manifest?: FileMeta; scoring_manifest?: FileMeta } | null;
} | null;

/** A feed that could not be read: every row says so, instead of "no run on record". */
function feedFailed(checks: RunCheck[], error: string | null | undefined): RunCheck[] {
  if (!error) return checks;
  return checks.map((c): RunCheck => ({ ...c, state: "error", note: `Could not read the run record (${error}).` }));
}

export function buildRunChecks(econ: EconLatestFeed, nowMs: number, error?: string | null): RunCheck[] {
  return feedFailed([
    evaluateRun(RUN_SPECS.econ_train, econ?.files?.manifest?.last_modified, nowMs),
    evaluateRun(RUN_SPECS.econ_score, econ?.files?.scoring_manifest?.last_modified, nowMs),
  ], error);
}
